import React, { useEffect, useMemo, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import "./WordGridQuestion.css";

const normalizeAnswer = (value) =>
  String(value || "")
    .replace(/\s+/g, "")
    .toLocaleLowerCase("uk-UA");

const parseAnswerPattern = (answer) => {
  const letters = [];
  const source = String(answer || "").trim();

  for (let idx = 0; idx < source.length; idx += 1) {
    const char = source[idx];

    if (/\s/.test(char)) continue;

    if (char === "[" && source[idx + 2] === "]" && source[idx + 1]) {
      letters.push({ value: source[idx + 1], revealed: true });
      idx += 2;
      continue;
    }

    if (char !== "[" && char !== "]") {
      letters.push({ value: char, revealed: false });
    }
  }

  return letters;
};

const normalizeRows = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row, idx) => {
      const answer = String(row?.answer || "").trim();
      const letters = parseAnswerPattern(answer);
      return {
        id: row?.id || `row-${idx + 1}`,
        clue: String(row?.clue || "").trim(),
        offset: Number(row?.offset) || 0,
        answer: letters.map((letter) => letter.value).join(""),
        letters,
      };
    })
    .filter((row) => row.letters.length > 0);

const WordGridQuestion = ({
  question,
  instruction,
  wordRows,
  revealedLetterColor,
  onSolved,
  showNextButton,
  onNext,
  showFinishButton,
  onFinish,
}) => {
  const rows = useMemo(() => normalizeRows(wordRows), [wordRows]);
  const minOffset = useMemo(() => Math.min(0, ...rows.map((row) => row.offset)), [rows]);
  const rowsWithLayout = useMemo(
    () => rows.map((row) => ({ ...row, startColumn: row.offset - minOffset })),
    [minOffset, rows]
  );
  const totalColumns = useMemo(
    () => Math.max(1, ...rowsWithLayout.map((row) => row.startColumn + row.letters.length)),
    [rowsWithLayout]
  );
  const revealedColor = String(revealedLetterColor || "").trim() || "#c62828";
  const solvedRef = useRef(false);

  const [rowValues, setRowValues] = useState([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setRowValues(rowsWithLayout.map((row) => row.letters.map((letter) => (letter.revealed ? letter.value : ""))));
    setTouched(false);
    solvedRef.current = false;
  }, [rowsWithLayout]);

  const rowStatuses = useMemo(
    () =>
      rows.map((row, rowIdx) => {
        const value = normalizeAnswer((rowValues[rowIdx] || []).join(""));
        const expected = normalizeAnswer(row.answer);
        if (!value) return "empty";
        if (value.length < expected.length) return "progress";
        return value === expected ? "correct" : "incorrect";
      }),
    [rows, rowValues]
  );

  const allRowsCorrect = rows.length > 0 && rowStatuses.every((status) => status === "correct");
  const solved = allRowsCorrect;

  useEffect(() => {
    if (!solved || solvedRef.current) return;
    solvedRef.current = true;
    onSolved?.();
  }, [solved, onSolved]);

  const setCell = (rowIdx, colIdx, rawValue) => {
    if (rows[rowIdx]?.letters[colIdx]?.revealed) return;

    const nextChar = Array.from(String(rawValue || "").trim()).pop() || "";

    setRowValues((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIdx) return row;
        return row.map((cell, cellIdx) => (cellIdx === colIdx ? nextChar : cell));
      })
    );
    setTouched(true);

    if (nextChar) {
      const nextEditableIndex = rows[rowIdx]?.letters.findIndex(
        (letter, idx) => idx > colIdx && !letter.revealed
      );
      const nextInput = nextEditableIndex >= 0
        ? document.querySelector(`[data-word-grid-cell="${rowIdx}-${nextEditableIndex}"]`)
        : null;
      nextInput?.focus();
    }
  };

  const handleKeyDown = (event, rowIdx, colIdx) => {
    if (event.key !== "Backspace" || rows[rowIdx]?.letters[colIdx]?.revealed || rowValues[rowIdx]?.[colIdx]) return;

    const previousEditableIndex = rows[rowIdx]?.letters
      .map((letter, idx) => ({ letter, idx }))
      .filter((item) => item.idx < colIdx && !item.letter.revealed)
      .pop()?.idx;
    const previousInput = previousEditableIndex >= 0 ? document.querySelector(
      `[data-word-grid-cell="${rowIdx}-${previousEditableIndex}"]`
    ) : null;
    previousInput?.focus();
  };

  if (!rows.length) {
    return <div className="word-grid-empty">Для завдання треба додати хоча б один рядок.</div>;
  }

  return (
    <div className="word-grid-question">
      {String(question || "").trim() && (
        <div className="word-grid-question-text" dangerouslySetInnerHTML={{ __html: question }} />
      )}

      {String(instruction || "").trim() && (
        <div className="word-grid-instruction">
          <strong>Як виконувати</strong>
          <p>{instruction}</p>
        </div>
      )}

      <div className="word-grid-layout">
        <div
          className="word-grid-board"
          style={{ "--word-grid-total-columns": totalColumns }}
        >
          {rowsWithLayout.map((row, rowIdx) => (
            <div className="word-grid-row" key={row.id}>
              <div className="word-grid-row-number">{rowIdx + 1}</div>
              <div
                className="word-grid-cells"
                style={{ "--word-grid-row-start": row.startColumn }}
              >
                {row.letters.map((letter, colIdx) => (
                  <input
                    key={`${row.id}-${colIdx}`}
                    data-word-grid-cell={`${rowIdx}-${colIdx}`}
                    className={`word-grid-cell${letter.revealed ? " is-revealed" : ""}`}
                    style={{
                      gridColumnStart: row.startColumn + colIdx + 1,
                      ...(letter.revealed ? { color: revealedColor } : {}),
                    }}
                    value={rowValues[rowIdx]?.[colIdx] || ""}
                    onChange={(event) => setCell(rowIdx, colIdx, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(event, rowIdx, colIdx)}
                    maxLength={2}
                    readOnly={letter.revealed}
                    aria-label={`Рядок ${rowIdx + 1}, літера ${colIdx + 1}`}
                  />
                ))}
              </div>
              <div className={`word-grid-row-status is-${rowStatuses[rowIdx]}`}>
                {rowStatuses[rowIdx] === "correct" && <CheckIcon fontSize="inherit" />}
              </div>
            </div>
          ))}
        </div>

        <div className="word-grid-clues">
          {rows.map((row, idx) => (
            <div className="word-grid-clue" key={`${row.id}-clue`}>
              <span>{idx + 1}.</span>
              <p>{row.clue || "Підказку не додано."}</p>
            </div>
          ))}
        </div>
      </div>

      {touched && (
        <div className={`word-grid-message${solved ? " is-correct" : ""}`}>
          {solved ? "Правильно." : "Заповніть усі клітинки правильно."}
        </div>
      )}

      {solved && showNextButton && (
        <button type="button" className="btn nextAnswer" onClick={onNext} style={{ display: "inline-block" }}>
          Наступне завдання
        </button>
      )}

      {solved && showFinishButton && (
        <button type="button" className="btn nextAnswer" onClick={onFinish} style={{ display: "inline-block" }}>
          Завершити
        </button>
      )}
    </div>
  );
};

export default WordGridQuestion;
